import {inject, Injectable} from '@angular/core';
import {TicketsService} from '../services/tickets.service';
import {TicketsStore} from '../stores/tickets.store';
import {
	Ticket,
	TICKET_CATEGORIES_LABEL,
	TICKET_STATUSES_LABELS,
	TicketAggregate,
	TicketCategory,
	TicketPriority,
	TicketStatus,
	TicketVM
} from '../models/ticket.model';
import {TicketTag} from '../models/ticket-tag.model';
import {optimisticUpdate} from '../../../shared/utils/optimistic-update.utils';
import {UserProfileStore} from '../../../core/auth/stores/user-profile.store';
import {UsersContext} from '../../users/users.context';
import {DashboardContext} from '../../dashboard/dashboard-context';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TicketEvent} from '../models/ticket-event.model';
import {TicketEventVM} from '../models/ticket-event.vm';
import {TICKET_PRIORITY_CONFIG} from '../components/ticket-priority-changer/ticket-priority-changer.config';
import {TagsContext} from '../contexts/tags.context';

@Injectable({ providedIn: 'root' })
export class TicketsFacade {
	private sanitizer = inject(DomSanitizer)

	private store: TicketsStore = inject(TicketsStore)
	private userProfileStore = inject(UserProfileStore)

	private userCtx = inject(UsersContext)
	private dashboardCtx = inject(DashboardContext)
	private tagsCtx = inject(TagsContext)

	private service: TicketsService = inject(TicketsService)

	loadDashboardStats() {
		this.store.setDashboardStatsLoading(true);
		this.store.setError(null);

		this.service.loadDashboardStats().subscribe({
			next: stats => {
				this.store.setDashboardStats(stats);
				this.store.setDashboardStatsLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento statistiche dashboard');
				this.store.setDashboardStatsLoading(false);
			}
		});
	}

	loadByCategory(category: TicketCategory) {
		this.store.setLoading(true);
		this.store.setError(null);

		this.service.loadTicketsByCategory(category).subscribe({
			next: tickets => {
				this.store.setTickets(tickets);
				this.store.setLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento ticket');
				this.store.setLoading(false);
			}
		});
	}

	loadDashboardTickets() {
		this.store.setDashboardTicketsLoading(true);
		this.store.setError(null);

		this.service.loadDashboardTickets().subscribe({
			next: tickets => {
				this.store.setDashboardTickets(tickets);
				this.store.setDashboardTicketsLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento ticket dashboard');
				this.store.setDashboardTicketsLoading(false);
			}
		});
	}

	loadDashboardCategorizationTickets() {
		this.store.setDashboardCategorizationLoading(true);
		this.store.setError(null);

		this.service.loadDashboardCategorizationTickets().subscribe({
			next: tickets => {
				this.store.setDashboardCategorizationTickets(tickets);
				this.store.setDashboardCategorizationLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento ticket da categorizzare');
				this.store.setDashboardCategorizationLoading(false);
			}
		});
	}

	loadAssigneeRequests() {
		this.store.setLoading(true);
		this.store.setError(null);

		this.service.loadAssigneeRequests().subscribe({
			next: assigneeRequests => {
				this.store.setAssigneeRequests(assigneeRequests);
				this.store.setLoading(false);
			},
			error: () => {
				this.store.setError('Errore nel caricamento richieste di assegnazione ticket');
				this.store.setLoading(false);
			}
		})
	}

	private getTicketOrReturn(ticketId: string) {
		return [
			...this.store.tickets(),
			...this.store.dashboardTickets()
		].find(t => t.id === ticketId);
	}

	loadTicketDetail(ticketId: string) {
		this.store.setLoading(true);

		this.service.getTicket(ticketId).subscribe({
			next: updated => {
				this.store.updateTicket(updated);
				this.store.setLoading(false);
			},
			error: () => {
				this.store.setError('Errore assegnazione ticket');
				this.store.setLoading(false);
			}
		});
	}

	loadTicketEmails(ticketId: string) {
		this.store.setEmailsLoading(ticketId, true);

		this.service.loadTicketEmails(ticketId).subscribe({
			next: emails => {
				const enriched = emails.map( e => ({
					...e,
					isCollapsed: false,
					isReplying: false
				}));

				this.store.setTicketEmails(ticketId, enriched);
				this.resetUnreadEmailsIfNeeded(ticketId);
			},
			error: () => {
				this.store.setEmailsLoading(ticketId, false);
			}
		});
	}

	loadTicketEvents(ticketId: string) {
		this.store.setEventsLoading(ticketId, true);

		this.service.loadTicketEvents(ticketId).subscribe({
			next: events => {
				this.store.setTicketEvents(ticketId, events);
			},
			error: () => {
				this.store.setEventsLoading(ticketId, false);
			}
		});
	}

	loadMergedTickets() {
		this.store.setLoading(true)
		this.store.setError(null)

		this.service.loadTargetMergeTickets().subscribe({
			next: tickets => {
				this.store.setMergeTargets(tickets)
				this.store.setLoading(false)
			},
			error: () => {
				this.store.setError('Errore unione Ticket')
				this.store.setLoading(false)
			}
		})
	}


	private resetUnreadEmailsIfNeeded(ticketId: string) {
		const ticket = this.store.getTicketById(ticketId);

		if (!ticket || ticket.unread_inbound_count === 0) return;

		this.service.resetUnreadEmails(ticketId).subscribe({
			next: () => {
				this.store.updateTicket({
					id: ticketId,
					unread_inbound_count: 0
				} as Ticket);
			}
		});
	}


	ensureTicketEnrichment(ticketId: string) {
		this.loadTicketEmails(ticketId);
		this.loadTicketEvents(ticketId);
	}

	ticketVMFromAggregate(aggregate: TicketAggregate): TicketVM {
		const vm = this.mapToVM(aggregate.core);

		return {
			...vm,
			pendingAssigneeChange: this.dashboardCtx.pendingTicketsListAssigneeChange().includes(aggregate.core.id),
			events: this.mapEventsToVM(aggregate.events),
			emails: aggregate.emails
		};
	}

	getTicketAggregate(ticketId: string): TicketAggregate | undefined {
		const ticket = this.store.getTicketById(ticketId);

		if (!ticket) return;

		return {
			core: ticket,
			events: ticket.events,
			eventsLoaded: ticket.eventsLoaded,
			eventsLoading: ticket.eventsLoading,
			emails: ticket.emails,
			emailsLoaded: ticket.emailsLoaded,
			emailsLoading: ticket.emailsLoading,
		};
	}

	ticketVMById(id: string): TicketVM | null {
		const ticket = this.store.tickets().find(t => t.id === id);
		if (!ticket) return null;

		return this.mapToVM(ticket);
	}

	assignTicket(ticketId: string, userId: string, reason?: string, newStatus?: TicketStatus) {
		const role = this.userProfileStore.effectiveRole();
		const ticket = this.ticketVMById(ticketId)

		if(!ticket) return;

		if (role === 'TENANT_USER' && ticket.isAssigned) {
			this.requestTicketAssignment(ticketId, userId, reason);
			return;
		}

		this.assignTicketDirect(ticketId, userId, newStatus);
	}

	private mapEventsToVM(events?: TicketEvent[]): TicketEventVM[] {
		return events?.map(e => this.mapEventToVM(e)) ?? [];
	}

	private mapEventToVM(event: TicketEvent): TicketEventVM {
		const userMap = this.userCtx.usersMap()
		const createdBy = userMap[event.created_by];

		const base = {
			id: event.id,
			ticket_id: event.ticket_id,
			type: event.type,
			createdAt: event.created_at ?? null,
			createdByLabel: createdBy
				? `${createdBy.name} ${createdBy.surname}`
				: 'Sistema'
		};

		switch (event.type) {
			case 'ASSIGNMENT_CHANGE_DIRECT': {
				const newAssignee = userMap[event.new_assignee_user_id]
				const newAssigneeLabel = newAssignee ? `${newAssignee.name} ${newAssignee.surname}` : 'Sistema'

				return {
					...base,
					icon: 'pi pi-user-edit',
					title: 'Cambio assegnatario',
					tag: 'Diretto',
					severityTag: 'success',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							<i class="pi pi-arrow-right"></i> ${newAssigneeLabel}
						</div>
					`)
				}
			}

			case 'ASSIGNMENT_CHANGE_REQUESTED': {
				const requestAssignee = userMap[event.requested_assignee_user_id]
				const requestAssigneeLabel = requestAssignee ? `${requestAssignee.name} ${requestAssignee.surname}` : 'Sistema'

				return {
					...base,
					icon: 'pi pi-user-edit',
					title: 'Cambio assegnatario',
					tag: 'Richiesta',
					severityTag: 'info',
					reason: event.reason,
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							<i class="pi pi-arrow-right"></i> ${requestAssigneeLabel}
						</div>
					`)
				}
			}

			case 'ASSIGNMENT_CHANGE_APPROVED': {
				const oldAssignee = userMap[event.old_assignee_user_id]
				const newAssignee = userMap[event.new_assignee_user_id]

				const oldAssigneeLabel = oldAssignee ? `${oldAssignee.name} ${oldAssignee.surname}` : 'Sistema'
				const newAssigneeLabel = newAssignee ? `${newAssignee.name} ${newAssignee.surname}` : 'Sistema'

				return {
					...base,
					icon: 'pi pi-check',
					title: 'Cambio assegnatario',
					tag: 'Approvato',
					severityTag: 'success',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							${oldAssigneeLabel} <i class="pi pi-arrow-right"></i> ${newAssigneeLabel}
						</div>
					`)
				}
			}

			case 'ASSIGNMENT_CHANGE_REJECTED':
				return {
					...base,
					icon: 'pi pi-times',
					title: 'Cambio assegnatario',
					tag: 'Rifiutato',
					severityTag: 'danger'
				}

			case 'TAG_ADDED': {
				const tag = this.tagsCtx.getTagSnapshotById(event.tag_id);

				return {
					...base,
					icon: 'pi pi-tag',
					title: 'Tag',
					tag: 'Aggiunto',
					severityTag: 'success',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<span class="text-${tag?.color ?? 'gray-500'} uppercase">
						  ${ tag?.label ?? 'Tag non trovato' }
						</span>
					`)
				}
			}

			case 'TAG_REMOVED': {
				const tag = this.tagsCtx.getTagSnapshotById(event.tag_id);

				return {
					...base,
					icon: 'pi pi-tag',
					title: 'Tag',
					tag: 'Rimosso',
					severityTag: 'danger',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<span class="text-${tag?.color ?? 'gray-500'} uppercase">
						  ${ tag?.label ?? 'Tag non trovato' }
						</span>
					`)
				}
			}

			case 'MERGED': {
				const source = this.store.getTicketById(event.source_ticket_id) ?? null
				const target = this.store.getTicketById(event.target_ticket_id) ?? null

				const sourceLabel = source ? `${source.ticket_code} - ${source.subject}` : 'Ticket non trovato'
				const targetLabel = target ? `${target.ticket_code} - ${target.subject}` : 'Ticket non trovato'

				return {
					...base,
					icon: 'pi pi-clone',
					title: 'Ticket unito',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							${sourceLabel} <i class="pi pi-arrow-right text-xs"></i> ${targetLabel}
						</div>
					`)
				}
			}

			case 'STATUS_CHANGED': {
				const oldStatus = TICKET_STATUSES_LABELS[event.old_status];
				const newStatus = TICKET_STATUSES_LABELS[event.new_status];

				return {
					...base,
					icon: 'pi pi-sync',
					title: 'Cambio stato',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							${oldStatus} <i class="pi pi-arrow-right text-xs"></i> ${newStatus}
						</div>
					`)
				};
			}

			case 'PRIORITY_CHANGED': {
				const oldPriority = TICKET_PRIORITY_CONFIG[event.old_priority];
				const newPriority = TICKET_PRIORITY_CONFIG[event.new_priority];

				return {
					...base,
					icon: 'pi pi-flag',
					title: 'Cambio priorità',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							${oldPriority.label} <i class="pi pi-arrow-right text-xs"></i> ${newPriority.label}
						</div>
					`)
				};
			}

			case 'CATEGORY_CHANGED': {
				const oldCategory = TICKET_CATEGORIES_LABEL[event.old_category];
				const newCategory = TICKET_CATEGORIES_LABEL[event.new_category];

				return {
					...base,
					icon: 'pi pi-folder',
					title: 'Cambio categoria',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="flex gap-2 align-items-center">
							${oldCategory} <i class="pi pi-arrow-right text-xs"></i> ${newCategory}
						</div>
					`)
				};
			}

			case 'NOTE_INTERNAL': {
				const note = event.note ?? 'Nessuna nota'

				return {
					...base,
					icon: 'pi pi-comment',
					title: 'Nota interna',
					description: this.sanitizer.bypassSecurityTrustHtml(`
						<div class="p-3 my-2 border-round-2xl border-1 border-gray-200 border-solid">
							${note}
						</div>
					`)
				};
			}

			default:
				return {
					...base,
					icon: 'pi pi-info-circle',
					title: 'Evento non definito'
				};
		}
	}

	private assignTicketDirect(ticketId: string, userId: string, newStatus?: TicketStatus) {
		const prevTicket = this.getTicketOrReturn(ticketId);
		if (!prevTicket) return;

		const nextStatus =
			prevTicket.status === 'TO_DO'
				? 'ASSIGNED'
				: newStatus ?? undefined;

		optimisticUpdate({
			apply: () => this.store.updateTicket({
				id: ticketId,
				assignee_user_id: userId,
				status: nextStatus ?? prevTicket.status
			} as Ticket),

			rollback: () => this.store.updateTicket({
				id: ticketId,
				assignee_user_id: prevTicket.assignee_user_id,
				status: prevTicket.status
			} as Ticket),

			request: () =>
				this.service.assignTicketAndMaybeUpdateStatus(
					ticketId,
					userId,
					nextStatus
				),

			success: () => this.refreshTicketsAndDashboardStats(),

			error: () =>
				this.store.setError('Errore assegnazione ticket'),

			setLoading: l => this.store.setLoading(l)
		});
	}

	private requestTicketAssignment(ticketId: string, userId: string, reason?: string) {

		this.store.setLoading(true);

		this.service.sendTicketAssignmentRequest(ticketId, userId, reason ?? '')
			.subscribe({
				next: () => {
					this.store.setLoading(false);
					this.loadAssigneeRequests();
				},
				error: () => {
					this.store.setError('Errore richiesta assegnazione');
					this.store.setLoading(false);
				}
			});
	}

	private mapToVM(ticket: Ticket): TicketVM {

		const mergeMap = this.store.mergeMap();
		const mergedIds = mergeMap.get(ticket.id) ?? [];

		const usersMap = this.userCtx.usersMap();
		const assignedUser = usersMap[ticket.assignee_user_id];
		const isMergeTarget = mergeMap.has(ticket.id);
		const mergeCount = mergedIds.length;

		return {
			...ticket,
			events: undefined,
			assignedUser,
			assignedLabel: assignedUser
				? `${assignedUser.name} ${assignedUser.surname}`
				: 'Non assegnato',
			isAssigned: !!assignedUser,
			pendingAssigneeChange: false,
			isMergeTarget,
			mergedIds,
			mergeCount
		};
	}

	approveRequestTicketAssignment (ticketId: string, requestId: string, newUserId: string) {
		this.store.setLoading(true)
		this.store.setError(null)

		this.service.approveTicketAssignmentRequest(ticketId, requestId, newUserId).subscribe({
			next: () => {
				this.store.setLoading(false)
			},
			error: () => {
				this.store.setError('Errore approvazione richiesta')
				this.store.setLoading(false)
			}
		})
	}

	rejectRequestTicketAssignment (ticketId: string, requestId: string, reason?: string) {
		this.store.setLoading(true)
		this.store.setError(null)

		this.service.rejectTicketAssignmentRequest(ticketId, requestId, reason ?? '').subscribe({
			next: () => {

				this.store.setLoading(false)
			},
			error: () => {
				this.store.setError('Errore rifiuto richiesta')
				this.store.setLoading(false)
			}
		})
	}

	mergeTicket(ticketId: string, tagetTicketId: string) {
		const prevTicket = this.getTicketOrReturn(ticketId);
		if (!prevTicket) return;

		this.store.setLoading(true)
		this.store.setError(null)

		this.service.mergeTicket(ticketId, tagetTicketId).subscribe({
			next: () => {
				this.loadByCategory(prevTicket.category)
				this.loadDashboardStats()
				this.loadTicketEmails(ticketId)
				this.store.setLoading(false)
			},
			error: () => {
				this.store.setError('Errore unione Ticket')
				this.store.setLoading(false)
			}
		})
	}

	moveToSupport(ticketId: string) {

		const prevTicket = this.getTicketOrReturn(ticketId);
		if (!prevTicket) return;

		optimisticUpdate({

			apply: () => {
				this.store.updateTicket({
					id: ticketId,
					category: 'SUPPORT',
					status: 'TO_DO'
				} as Ticket);
			},

			rollback: () => {
				this.store.updateTicket({
					id: ticketId,
					category: prevTicket.category,
					status: prevTicket.status
				} as Ticket);
			},

			request: () => this.service.setTicketCategory(ticketId, 'SUPPORT'),

			success: () => {
				this.service.setTicketStatus(ticketId, 'TO_DO')
				this.refreshTicketsAndDashboardStats()
			},

			error: () => this.store.setError('Errore spostamento ticket'),

			setLoading: l => this.store.setLoading(l)
		});
	}

	addNote(ticketId: string, note: SafeHtml) {
		this.store.setLoading(true);

		this.service.setTicketNote(ticketId, note).subscribe({
			next: () => {
				this.loadTicketEvents(ticketId);
				this.store.setLoading(false);
			},
			error: () => {
				this.store.setError('Errore aggiunta nota ticket');
				this.store.setLoading(false);
			}
		});
	}

	updateCategory(ticketId: string, category: TicketCategory) {

		const prevTicket = this.getTicketOrReturn(ticketId)
		if (!prevTicket) return;

		const prevCategory = prevTicket.category;

		optimisticUpdate({

			apply: () => this.store.updateTicket({ id: ticketId, category } as Ticket),

			rollback: () => this.store.updateTicket({ id: ticketId, category: prevCategory } as Ticket),

			request: () => this.service.setTicketCategory(ticketId, category),

			success: () => this.refreshTicketsAndDashboardStats(),

			error: () => this.store.setError('Errore aggiornamento categoria ticket'),

			setLoading: l => this.store.setLoading(l)
		});
	}

	updateStatus(ticketId: string, status: TicketStatus) {

		const prevTicket = this.getTicketOrReturn(ticketId)
		if (!prevTicket) return

		const prevStatus = prevTicket.status;

		optimisticUpdate({

			apply: () => this.store.updateTicket({ id: ticketId, status } as Ticket),

			rollback: () => this.store.updateTicket({ id: ticketId, status: prevStatus } as Ticket),

			request: () => this.service.setTicketStatus(ticketId, status),

			success: () => this.refreshTicketsAndDashboardStats(),

			error: () => 	this.store.setError('Errore aggiornamento Stato ticket'),

			setLoading: l => this.store.setLoading(l)
		})
	}

	updatePriority(ticketId: string, priority: TicketPriority) {
		const prevTicket = this.getTicketOrReturn(ticketId)
		if (!prevTicket) return

		const prevPriority = prevTicket.priority;

		optimisticUpdate({

			apply: () => this.store.updateTicket({ id: ticketId, priority } as Ticket),

			rollback: () => this.store.updateTicket({ id: ticketId, priority: prevPriority } as Ticket),

			request: () => this.service.setTicketPriority(ticketId, priority),

			success: () => this.loadByCategory(prevTicket.category),

			error: () => this.store.setError('Errore aggiornamento priorità ticket'),

			setLoading: l => this.store.setLoading(l)
		});
	}

	attachTagToTicket(ticketId: string, tag: TicketTag) {
		const prevTicket = this.getTicketOrReturn(ticketId);
		if (!prevTicket) return;

		if (prevTicket.tags.some(t => t.id === tag.id)) return;

		optimisticUpdate({
			apply: () => {
				this.store.updateTicket({
					id: ticketId,
					tags: [...prevTicket.tags, tag]
				} as Ticket);
			},

			rollback: () => {
				this.store.updateTicket({
					id: ticketId,
					tags: prevTicket.tags
				} as Ticket);
			},

			request: () => this.service.attachTag(ticketId, tag.id),

			success: () => this.loadTicketEvents(ticketId),

			error: () => {
				this.store.setError('Errore associazione tag');
			},

			setLoading: l => this.store.setLoading(l)
		});
	}

	removeTag(ticketId: string, tagId: string) {
		const prevTicket = this.getTicketOrReturn(ticketId);
		if (!prevTicket) return;

		if (!prevTicket.tags.some(t => t.id === tagId)) return;

		optimisticUpdate({
			apply: () => {
				this.store.updateTicket({
					id: ticketId,
					tags: prevTicket.tags.filter(t => t.id !== tagId)
				} as Ticket);
			},

			rollback: () => {
				this.store.updateTicket({
					id: ticketId,
					tags: prevTicket.tags
				} as Ticket);
			},

			request: () => this.service.removeTag(ticketId, tagId),

			success: () => this.loadTicketEvents(ticketId),

			error: () => {
				this.store.setError('Errore rimozione tag');
			},

			setLoading: l => this.store.setLoading(l)
		});
	}

	clear() {
		this.store.setTickets([]);
	}

	private refreshTicketsAndDashboardStats() {
		this.loadDashboardStats();
		this.loadDashboardTickets();
	}
}
